import { AuthWorkspace, authWorkspaceConnector } from '@harmony/pcportal-shell-library';

import { HealthStatus, Workspace } from '../../../Common/Constants';
import { HttpResponse } from '../../../Services/Services.Primary/HttpService/Models/HttpServiceModels';
import {
  ServiceIncident,
  ServiceIncidentList,
} from '../../../Services/Services.Primary/HttpService/Models/ServiceIncident';
import { SupportTopic } from '../../../Services/Services.Primary/HttpService/Models/SupportTopic';
import { PartnerSupportService } from '../../../Services/Services.Primary/HttpService/PartnerSupportService';
import LocalizationService from '../../../Services/Services.Secondary/Localization/LocalizationService';
import { isEmptyOrSpaces } from '../../../Utilities/Helpers';
import { getWorkspaceTagFromId } from '../../../Utilities/Helpers/Localization.Helper';
import { ServiceStatus } from '../Service Health/Models/ServiceHealthTables';
import { PreFetchService } from './PreFetchService';

const resx = LocalizationService.getInstance();

export class ServiceHealthHelper {
  private static instance: ServiceHealthHelper;
  private partnerSupportService: PartnerSupportService = PartnerSupportService.getInstance();
  private topicWorkspaceMapping: Map<string, string> | undefined;
  private preFetchAuthWorkpsaceId = 'preFetchAuthWorkspace';
  private preFetchService = PreFetchService.getInstance();

  public static getInstance = () => {
    if (!ServiceHealthHelper.instance) {
      ServiceHealthHelper.instance = new ServiceHealthHelper();
    }
    return ServiceHealthHelper.instance;
  };

  private getWorkspaceName = (serviceId: string) => {
    if (this.topicWorkspaceMapping && this.topicWorkspaceMapping.has(serviceId)) {
      return getWorkspaceTagFromId(this.topicWorkspaceMapping.get(serviceId) as string);
    }
    return '';
  };

  private getServiceHealthTopicName = (serviceId: string) => {
    const nameKey = 'ServiceHealthTopicName_' + serviceId;
    const name = resx.getLocalizedResource(nameKey);
    if (name !== nameKey) {
      return name;
    }

    return '';
  };

  public getServiceHealth = async (): Promise<Map<string, ServiceStatus>> => {
    const servicesMap = new Map<string, ServiceStatus>();
    try {
      const response = await this.partnerSupportService.getServiceHealthList();

      if (response.data && response.data.Data && response.data.Data.length > 0) {
        if (!this.topicWorkspaceMapping) {
          await this.getL2TopicWorkspaceMapping((await this.getSupportTopics()) ?? []);
        }
        const services = response.data.Data;
        for (let i = 0; i < services.length; i++) {
          for (const child of services[i].children) {
            const localizedChildName = this.getServiceHealthTopicName(child.id);
            const entry: ServiceStatus = {
              id: child.id,
              name: isEmptyOrSpaces(localizedChildName) ? child.name : localizedChildName,
              status: child.status,
              statusDescription: child.statusDescription,
              workspace: this.getWorkspaceName(child.id),
              serviceId: services[i].id,

              //TODO: actual last updated date
              //FYI: There is a '... days ago' method in Helper
              lastUpdated: '',
            };

            servicesMap.set(entry.id, entry);
          }
        }

        return servicesMap;
      }
    } catch (error) {
      //TODO log errors
    }

    return servicesMap;
  };

  public getServiceIncidents = async (): Promise<ServiceIncident[]> => {
    //TODO: need to pass continuationToken in request header
    let results: ServiceIncident[] = [];

    try {
      let response = (await this.partnerSupportService.getServiceIncidents()) as HttpResponse<ServiceIncidentList>;
      results = response.data?.item1 ?? [];

      while (response && response.data && !isEmptyOrSpaces(response.data.continuationToken)) {
        //TODO pass token
        response = (await this.partnerSupportService.getServiceIncidents()) as HttpResponse<ServiceIncidentList>;
        if (response && response.data && response.data.item1) {
          results = results.concat(response.data.item1);
        }
      }
    } catch (error) {
      //TODO: error logging
    }

    return results;
  };

  public getAuthWorkspace = async (): Promise<AuthWorkspace[]> => {
    const preFetchPromise = this.preFetchService.getPreFetchedPromise(this.preFetchAuthWorkpsaceId);
    if (preFetchPromise) {
      return preFetchPromise;
    }

    const authWorkspace = await authWorkspaceConnector();

    return authWorkspace;
  };

  public preFetchAuthWorkspace = () => {
    this.preFetchService.preFetchData(this.preFetchAuthWorkpsaceId, this.getAuthWorkspace);
  };

  public getSupportTopics = async (): Promise<SupportTopic[] | null> => {
    try {
      const response = await this.partnerSupportService.getSupportTopics();

      if (response && response.data && response.data.Data) {
        this.getL2TopicWorkspaceMapping(response.data.Data);
        return response.data.Data;
      }

      return null;
    } catch (error) {
      // Todo: error logging
      return null;
    }
  };

  /**
   * Extracts workspace from a support topic's metadata
   * @param topic The support topic to extract workspace from
   * @returns The workspace string or empty string if not found
   */
  private extractWorkspaceFromTopic = (topic: SupportTopic): string => {
    if (!topic || !topic.metadata) {
      return '';
    }
    if (
      topic.metadata.workspace &&
      topic.metadata.workspace.length > 0 &&
      topic.metadata.workspace[0] !== Workspace.None
    ) {
      return topic.metadata.workspace[0];
    } else if (
      topic.metadata.workspaceCategory &&
      topic.metadata.workspaceCategory.length > 0 &&
      topic.metadata.workspaceCategory[0] !== Workspace.None
    ) {
      return topic.metadata.workspaceCategory[0];
    }
    return '';
  };

   /**
   * Determines the workspace for an L3 topic, with fallback to parent L2 topic
   * @param l3Topic The L3 topic
   * @param parentL2Topic The parent L2 topic for fallback
   * @returns The workspace string or empty string if not found
   */
  private getL3TopicWorkspace = (l3Topic: SupportTopic, parentL2Topic: SupportTopic): string => {
    // First try to get workspace from L3 topic itself
    const l3Workspace = this.extractWorkspaceFromTopic(l3Topic);
    if (l3Workspace) {
      return l3Workspace;
    }
    // Fallback to parent L2 topic workspace
    return this.extractWorkspaceFromTopic(parentL2Topic);
  };

  public getL2TopicWorkspaceMapping = async (supportTopics: SupportTopic[]) => {
    // Map of L2 topic ID --> workspace
    const topicWorkspaceMapping = new Map<string, string>();

    if (supportTopics && supportTopics.length > 0) {
      supportTopics.forEach((supportTopic: SupportTopic) => {
        const workspace = this.extractWorkspaceFromTopic(supportTopic);
        if (workspace) {
          topicWorkspaceMapping.set(supportTopic.id, workspace);
        }
      });
    }
    this.topicWorkspaceMapping = topicWorkspaceMapping;
  };

  public getSortedList = (statusList: ServiceStatus[]) => {
    if (statusList) {
      const sortStatus = (a: ServiceStatus, b: ServiceStatus) => {
        if (a.status === b.status) {
          if (a.workspace === b.workspace) {
            return a.name.toLowerCase() < b.name.toLowerCase()
              ? -1
              : a.name.toLowerCase() > b.name.toLowerCase()
              ? 1
              : 0;
          }
          return a.workspace.toLowerCase() < b.workspace.toLowerCase() ? -1 : 1;
        } else {
          if (a.status === HealthStatus.Error.Code) return -1;
          if (b.status === HealthStatus.Error.Code) return 1;
          if (a.status === HealthStatus.Warning.Code) return -1;
          if (b.status === HealthStatus.Warning.Code) return 1;
          if (a.status === HealthStatus.Information.Code) return -1;
          return 1;
        }
      };

      statusList.sort(sortStatus);
    }
    return statusList;
  };

  /**
   * Retrieves the workspace ID and hierarchical topic names (L1, L2, L3) for a given topic ID.
   * This method searches through the support topic hierarchy and returns an object containing
   * the workspace ID, names for each hierarchy level, and a combined full path string.
   * If the topic is not found, all fields in the result will be empty strings.
   * @param topicId The topic ID to look up.
   * @returns An object with workspaceId, l1Name, l2Name, l3Name, and fullPath.
   */
  public getTopicHierarchyInfo = async (topicId: string): Promise<{
    workspaceId: string;
    l1Name: string;
    l2Name: string;
    l3Name: string;
    fullPath: string;
  }> => {
    const defaultResult = {
      workspaceId: '',
      l1Name: '',
      l2Name: '',
      l3Name: '',
      fullPath: ''
    };

    try {      
      const supportTopics = await this.getSupportTopics();
      if (!supportTopics?.length) {
        return defaultResult;
      }

      // Use a more efficient search approach
      return this.searchTopicHierarchy(supportTopics, topicId) || defaultResult;
    } catch (error) {
      return defaultResult;
    }
  };

  /**
   * Recursively searches through the topic hierarchy to find a topic by ID
   * @param topics Array of topics to search through
   * @param targetId The topic ID to find
   * @param parentNames Array of parent topic names for building the full path
   * @returns Topic hierarchy information or null if not found
   */
  private searchTopicHierarchy = (
    topics: SupportTopic[], 
    targetId: string, 
    parentNames: string[] = []
  ): {
    workspaceId: string;
    l1Name: string;
    l2Name: string;
    l3Name: string;
    fullPath: string;
  } | null => {
    for (const topic of topics) {
      if (!topic?.id) continue;

      const currentNames = [...parentNames, topic.name || ''];
      
      // Check if current topic matches the target ID
      if (topic.id === targetId) {
        const workspaceId = this.getTopicWorkspace(topic, topics, parentNames);
        
        return {
          workspaceId,
          l1Name: currentNames[0] || '',
          l2Name: currentNames[1] || '',
          l3Name: currentNames[2] || '',
          fullPath: currentNames.filter(name => name).join('/')
        };
      }

      // Recursively search subtopics if they exist
      if (topic.subtopics?.length && currentNames.length < 3) {
        const result = this.searchTopicHierarchy(topic.subtopics, targetId, currentNames);
        if (result) {
          return result;
        }
      }
    }

    return null;
  };

  /**
   * Gets the workspace for a topic based on its position in the hierarchy
   * @param topic The topic to get workspace for
   * @param siblingTopics Array of sibling topics for context
   * @param parentNames Parent topic names to determine hierarchy level
   * @returns The workspace string
   */
  private getTopicWorkspace = (
    topic: SupportTopic, 
    siblingTopics: SupportTopic[], 
    parentNames: string[]
  ): string => {
    // For L3 topics, use the specialized method with parent L2 topic
    if (parentNames.length === 2) {
      const parentL2Topic = this.findParentTopic(siblingTopics, parentNames);
      return parentL2Topic ? this.getL3TopicWorkspace(topic, parentL2Topic) : this.extractWorkspaceFromTopic(topic);
    }

    // For L1 and L2 topics, try current topic first, then fallback to parent
    const workspaceId = this.extractWorkspaceFromTopic(topic);
    if (workspaceId || parentNames.length === 0) {
      return workspaceId;
    }

    // Fallback to parent topic workspace for L2 topics
    const parentL1Topic = this.findParentTopic(siblingTopics, parentNames.slice(0, 1));
    return parentL1Topic ? this.extractWorkspaceFromTopic(parentL1Topic) : '';
  };

  /**
   * Finds a parent topic by traversing the hierarchy
   * @param rootTopics Root level topics to search from
   * @param targetPath Array of topic names representing the path to the parent
   * @returns The parent topic or null if not found
   */
  private findParentTopic = (rootTopics: SupportTopic[], targetPath: string[]): SupportTopic | null => {
    if (!targetPath.length || !rootTopics?.length) return null;

    for (const topic of rootTopics) {
      if (topic?.name === targetPath[0]) {
        if (targetPath.length === 1) {
          return topic;
        }
        if (topic.subtopics?.length) {
          return this.findParentTopic(topic.subtopics, targetPath.slice(1));
        }
      }
    }

    return null;
  };
}
